import React, {
  Suspense,
  useMemo,
  useReducer,
  useEffect,
  useCallback,
  useRef,
  useState,
} from "react";
import type { FC, Dispatch } from "react";

import { format } from "date-fns";
import {
  Box,
  Text,
  VStack,
  Avatar,
  useToast,
  Center,
  Button,
  ScrollView,
  Pressable,
  Tooltip,
  HStack,
} from "native-base";
import { useQueryLoader, usePreloadedQuery, useMutation } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import SideBar from "pianofunclub-crm/components/Drawers/SideBar";
import ItemStatus from "pianofunclub-crm/components/General/ItemStatus";
import EnrolmentStatusActionsheet from "pianofunclub-crm/components/Modals/EnrolmentStatusActionsheet";
import type { EnrolmentStatusActionsheetProps } from "pianofunclub-crm/components/Modals/EnrolmentStatusActionsheet";
import type {
  WaitingListStackNavigatorProps,
  WaitingListStackRouteProps,
} from "pianofunclub-crm/navigation/WaitingListNavigator";
import { getEnrolErrorToastText } from "pianofunclub-crm/utils/errorHandling";

import LoadingBlobs from "pianofunclub-shared/components/Animations/LoadingBlobs";
import TextInput from "pianofunclub-shared/components/Inputs/TextInput";
import Select from "pianofunclub-shared/components/NativeBaseExtended/Select";
import ToastAlert from "pianofunclub-shared/components/NativeBaseExtended/ToastAlert";
import { ArchivedIcon, TickIcon, UndoIcon } from "pianofunclub-shared/components/Other/Icons";
import { useData } from "pianofunclub-shared/providers/DataProvider";
import type {
  EditEnrolmentMutation,
  EditEnrolmentMutation$data,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/EditEnrolmentMutation.graphql";
import type {
  EnrolPupilMutation,
  EnrolPupilMutation$data,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/EnrolPupilMutation.graphql";
import type { LoadEnrolmentQuery } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadEnrolmentQuery.graphql";
import { edit_enrolment } from "pianofunclub-shared/relay/graphql/accounts/EditEnrolment";
import { enrol_pupil } from "pianofunclub-shared/relay/graphql/accounts/EnrolPupil";
import { load_enrolment } from "pianofunclub-shared/relay/graphql/accounts/LoadEnrolment";
import { INSTRUMENTS, SCHOOL_YEARS } from "pianofunclub-shared/utils/constants";
import { LESSON_STAGES, LESSON_TYPES } from "pianofunclub-shared/utils/constants";
import {
  getInitials,
  titleCaseConverter,
  getLessonBlockInfoString,
  getEnrolmentStatus,
} from "pianofunclub-shared/utils/converters";
import { getFullName } from "pianofunclub-shared/utils/extractors";
import { ensureDateType } from "pianofunclub-shared/utils/helpers";
import { createReducer } from "pianofunclub-shared/utils/reducers";
import type { Action, State } from "pianofunclub-shared/utils/reducers";

export type NavigationProps = WaitingListStackNavigatorProps<"Enrolment">;
export type RouteProps = WaitingListStackRouteProps<"Enrolment">;

interface ScreenProps {
  navigation: NavigationProps;
  route: RouteProps;
}

interface ContentProps {
  dispatchState: Dispatch<Action<ReducerValues>>;
  loadEnrolmentQueryReference: PreloadedQuery<LoadEnrolmentQuery, Record<string, unknown>>;
  navigation: NavigationProps;
  route: RouteProps;
  state: State<ReducerValues>;
}

export type ReducerValues = {
  adminNotes?: string;
  changeStatusActionsheetIsOpen: boolean;
  deleteEnrolmentAlertIsOpen?: boolean;
  hasBeenEdited: boolean;
  instrumentSelect?: string;
  lessonStageSelect: string;
  lessonTypeSelect?: string;
  parentEmail?: string;
  parentName?: string;
  parentPhoneNumber?: string;
  positionInWaitingList?: string;
  pupilFirstName?: string;
  pupilLastName?: string;
  pupilNotes?: string;
  schoolClass?: string;
  schoolIdSelect?: string;
  schoolYearSelect?: string;
  sentWaitingListEmail?: boolean;
};

const SIDE_BAR_WIDTH = 12;

const EnrolmentContent: FC<ContentProps> = (props) => {
  const { dispatchState, loadEnrolmentQueryReference, navigation, route, state } = props;

  const enrolmentData = usePreloadedQuery(load_enrolment, loadEnrolmentQueryReference);

  const toast = useToast();

  const { dataState } = useData();

  const allSchools = useMemo(() => {
    return dataState.values.schools?.edges ?? [];
  }, [dataState.values.schools?.edges]);

  const enrolmentStatus = useMemo(() => {
    return getEnrolmentStatus({
      lastOfferedTimestamp: enrolmentData.enrolment?.lastOfferedTimestamp ?? "",
      schoolArchived: enrolmentData.enrolment?.school?.archived ?? false,
      status: enrolmentData.enrolment?.status,
    });
  }, [enrolmentData.enrolment]);

  const [commitEditEnrolment] = useMutation<EditEnrolmentMutation>(edit_enrolment);

  const [commitEnrolPupil, enrolPupilInFlight] = useMutation<EnrolPupilMutation>(enrol_pupil);

  const hasInitialisedLocalState = useRef(false);

  const [initialEnrolmentData, setInitialEnrolmentData] = useState<Partial<ReducerValues>>();

  useEffect(() => {
    if (!enrolmentData.enrolment) {
      return;
    }

    const updatedState = {
      adminNotes: enrolmentData.enrolment.adminNotes ?? "",
      instrumentSelect: enrolmentData.enrolment.instrument ?? undefined,
      lessonStageSelect: enrolmentData.enrolment.lessonStage ?? "-1",
      lessonTypeSelect: enrolmentData.enrolment.lessonType ?? undefined,
      parentEmail: enrolmentData.enrolment.parentEmail ?? "",
      parentName: enrolmentData.enrolment.parentName ?? "",
      parentPhoneNumber: enrolmentData.enrolment.parentPhoneNumber ?? "",
      positionInWaitingList: enrolmentData.enrolment.positionInWaitingList?.toString() ?? "",
      pupilFirstName: enrolmentData.enrolment.pupilFirstName ?? "",
      pupilLastName: enrolmentData.enrolment.pupilLastName ?? "",
      pupilNotes: enrolmentData.enrolment.pupilNotes ?? "",
      schoolClass: enrolmentData.enrolment.schoolClass ?? undefined,
      schoolIdSelect: enrolmentData.enrolment.school?.id ?? undefined,
      schoolYearSelect: enrolmentData.enrolment.schoolYear?.toString() ?? undefined,
    };

    dispatchState({
      input: (currentValues) => ({
        ...currentValues,
        ...updatedState,
      }),
    });

    if (!hasInitialisedLocalState.current) {
      setInitialEnrolmentData(updatedState);
      hasInitialisedLocalState.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolmentData.enrolment]);

  const editEnrolment = useCallback<EnrolmentStatusActionsheetProps["editEnrolment"]>(
    (variables, options) => {
      dispatchState({
        input: "hasBeenEdited",
        value: true,
      });

      const editEnrolmentConfig = {
        variables: {
          input: {
            ...variables,
            enrolmentId: route.params.enrolmentId,
          },
        },
        onCompleted: (response: EditEnrolmentMutation$data) => {
          if (response?.editEnrolment?.success) {
            if (options?.isUndo) {
              dispatchState({
                input: "hasBeenEdited",
                value: false,
              });
              toast.show({
                render: ({ id }: { id: string }) => (
                  <ToastAlert id={id} status={"info"} title={"Changes undone"} toast={toast} />
                ),
              });
            }
          } else {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Couldn't update enrolment"}
                  toast={toast}
                />
              ),
            });
          }
        },
      };

      commitEditEnrolment(editEnrolmentConfig);
    },
    [commitEditEnrolment, dispatchState, route.params.enrolmentId, toast]
  );

  const enrolPupil = useCallback<EnrolmentStatusActionsheetProps["enrolPupil"]>(
    (variables) => {
      const enrolPupilConfig = {
        variables: {
          input: {
            enrolmentId: route.params.enrolmentId,
          },
        },
        onCompleted: (response: EnrolPupilMutation$data) => {
          if (response?.enrolPupil?.success) {
            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  id={id}
                  status="success"
                  title={"Successfully enroled pupil"}
                  toast={toast}
                />
              ),
            });

            variables?.onComplete?.();
          } else {
            const { description, title } = getEnrolErrorToastText(response);

            toast.show({
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={description}
                  id={id}
                  status="error"
                  title={title}
                  toast={toast}
                />
              ),
            });
          }
        },
      };

      commitEnrolPupil(enrolPupilConfig);
    },
    [commitEnrolPupil, route.params.enrolmentId, toast]
  );

  const undoChanges = useCallback(() => {
    if (!initialEnrolmentData) {
      return;
    }

    dispatchState({
      input: (currentValues) => ({
        ...currentValues,
        ...initialEnrolmentData,
      }),
    });

    editEnrolment(
      {
        adminNotes: initialEnrolmentData.adminNotes,
        instrument: initialEnrolmentData.instrumentSelect,
        lessonStage: initialEnrolmentData.lessonStageSelect,
        lessonType: initialEnrolmentData.lessonTypeSelect,
        parentEmail: initialEnrolmentData.parentEmail,
        parentName: initialEnrolmentData.parentName,
        parentPhoneNumber: initialEnrolmentData.parentPhoneNumber,
        positionInWaitingList:
          typeof initialEnrolmentData.positionInWaitingList === "string"
            ? parseInt(initialEnrolmentData.positionInWaitingList)
            : undefined,
        pupilFirstName: initialEnrolmentData.pupilFirstName,
        pupilLastName: initialEnrolmentData.pupilLastName,
        pupilNotes: initialEnrolmentData.pupilNotes,
        schoolClass: initialEnrolmentData.schoolClass,
        schoolId: initialEnrolmentData.schoolIdSelect,
        schoolYear:
          typeof initialEnrolmentData.schoolYearSelect === "string"
            ? parseInt(initialEnrolmentData.schoolYearSelect)
            : undefined,
      },
      { isUndo: true }
    );
  }, [dispatchState, editEnrolment, initialEnrolmentData]);

  const inputFinishEditingHandler = useCallback(
    (inputIdentifier?: string | number, inputValue?: string, isValid?: boolean) => {
      if (
        isValid &&
        inputIdentifier &&
        inputValue !== undefined &&
        inputValue !==
          enrolmentData.enrolment?.[inputIdentifier as keyof typeof enrolmentData.enrolment] &&
        inputValue !==
          enrolmentData.enrolment?.[inputIdentifier as keyof typeof enrolmentData.enrolment]
      ) {
        let parsedInputValue: string | number = inputValue?.trim();
        dispatchState({
          input: inputIdentifier as keyof ReducerValues,
          value: parsedInputValue,
        });

        if (inputIdentifier === "positionInWaitingList") {
          parsedInputValue = Number(inputValue);
        }
        editEnrolment({
          [inputIdentifier]: parsedInputValue,
        });
      }
    },
    [enrolmentData, dispatchState, editEnrolment]
  );

  const selectChangeHandler = useCallback(
    (inputIdentifier: string, itemValue: string | boolean) => {
      if (state.values?.[`${inputIdentifier}Select` as keyof ReducerValues] != itemValue) {
        dispatchState({
          input: `${inputIdentifier}Select` as keyof ReducerValues,
          value: itemValue,
        });

        let parsedItemValue: string | number | boolean = itemValue;
        if (inputIdentifier === "schoolYear") {
          parsedItemValue = Number(itemValue);
        }

        editEnrolment({
          [inputIdentifier]: parsedItemValue,
        });
      }
    },
    [state.values, dispatchState, editEnrolment]
  );

  const renderEnrolmentStatusPressable = useMemo(() => {
    return (
      <Pressable
        alignItems="center"
        onPress={() =>
          dispatchState({
            input: "changeStatusActionsheetIsOpen",
            value: true,
          })
        }
      >
        <ItemStatus showEditArrow status={enrolmentStatus} />
      </Pressable>
    );
  }, [dispatchState, enrolmentStatus]);

  const renderContent = useMemo(() => {
    return (
      <ScrollView
        bg="surface.100"
        contentContainerStyle={{
          alignItems: "center",
          paddingBottom: 40,
          paddingTop: 100,
        }}
      >
        <VStack
          alignItems="center"
          bg="surface.200"
          borderColor="surface.400"
          borderRadius="3xl"
          borderWidth={1}
          p="4"
          shadow={1}
          space="4"
          width="400px"
        >
          <VStack alignItems="center" width="100%">
            <Button
              borderColor="surface.400"
              borderRadius="xl"
              isDisabled={!state.values.hasBeenEdited}
              left="-4px"
              leftIcon={
                <Center size="7">
                  <UndoIcon color="surface.800" ml="2px" size="5" />
                </Center>
              }
              onPress={undoChanges}
              padding="3px"
              position="absolute"
              top="-4px"
              variant="outline"
            />
            <Text fontSize="lg" marginBottom="4">
              Enrolment
            </Text>
            <Avatar
              _text={{ color: "surface.200" }}
              bg="primary.600"
              size="md"
              source={{
                uri: undefined,
              }}
            >
              {getInitials(
                enrolmentData.enrolment?.pupilFirstName ?? "",
                enrolmentData.enrolment?.pupilLastName ?? ""
              )}
            </Avatar>
            <Text fontSize="xl" mt="2" textAlign="center">
              {getFullName(
                enrolmentData.enrolment?.pupilFirstName ?? "",
                enrolmentData.enrolment?.pupilLastName ?? ""
              )}
            </Text>
            <VStack alignItems="center" pt="4" space="2">
              {renderEnrolmentStatusPressable}
              {enrolmentData.enrolment?.createdTimestamp && (
                <Text color="gray.500" fontSize="sm" mt="2">
                  {`Added to list: ${format(
                    new Date(enrolmentData.enrolment?.createdTimestamp),
                    "dd/MM/yyyy HH:mm"
                  )}`}
                </Text>
              )}
              {enrolmentData.enrolment?.offers.edges?.map((offer) => {
                if (!offer?.node?.id) {
                  return null;
                }

                const lessonBlock = offer.node.lessonBlock;

                let lessonBlockInfoString: string | undefined = undefined;

                if (lessonBlock) {
                  lessonBlockInfoString = getLessonBlockInfoString({
                    instrumentName: lessonBlock?.instrument?.name,
                    lessonDay: lessonBlock?.lessonDay,
                    lessonDuration: lessonBlock?.lessonStage?.lessonDuration,
                    lessonStage: lessonBlock?.lessonStage?.stage,
                    lessonTime: lessonBlock?.lessonTime,
                    lessonType: lessonBlock?.lessonStage?.lessonType,
                    teacherFullName: getFullName(
                      lessonBlock?.teacher?.user?.firstName,
                      lessonBlock?.teacher?.user?.lastName
                    ),
                  });
                }

                return (
                  <Tooltip
                    key={offer.node.id}
                    isDisabled={!lessonBlockInfoString}
                    label={lessonBlockInfoString ?? ""}
                  >
                    <Pressable
                      _disabled={{
                        opacity: 1,
                      }}
                      disabled={!lessonBlock?.teacher?.id}
                      onPress={() => {
                        if (lessonBlock?.teacher?.id) {
                          navigation.push("Account", {
                            accountType: "TEACHER",
                            dayIndex: lessonBlock?.lessonDay ?? undefined,
                            profileId: lessonBlock?.teacher?.id,
                          });
                        }
                      }}
                    >
                      <HStack alignItems="center" space="1">
                        <Text
                          color={
                            offer.node.offerAccepted
                              ? "primary.500"
                              : offer.node.archived
                                ? "gray.400"
                                : "gray.500"
                          }
                          fontSize="sm"
                        >
                          {`Offered slot: ${format(
                            ensureDateType(offer.node.offerTimestamp),
                            "dd/MM/yyyy HH:mm"
                          )}`}
                        </Text>
                        {offer.node.offerAccepted ? (
                          <TickIcon color="primary.500" />
                        ) : offer.node.archived ? (
                          <ArchivedIcon color="gray.400" />
                        ) : null}
                      </HStack>
                    </Pressable>
                  </Tooltip>
                );
              })}
              {enrolmentData.enrolment?.enroledTimestamp && (
                <Text color="primary.600" fontSize="sm">
                  {`Enroled: ${format(
                    new Date(enrolmentData.enrolment?.enroledTimestamp),
                    "dd/MM/yyyy HH:mm"
                  )}`}
                </Text>
              )}
              {enrolmentData.enrolment?.archivedTimestamp && (
                <Text color="gray.500" fontSize="sm">
                  {`Archived: ${format(
                    new Date(enrolmentData.enrolment?.archivedTimestamp),
                    "dd/MM/yyyy HH:mm"
                  )}`}
                </Text>
              )}
              {enrolmentData.enrolment?.enroledPupil?.id && (
                <Pressable
                  _hover={{
                    bg: "surface.400",
                  }}
                  _pressed={{
                    bg: "surface.500",
                  }}
                  borderRadius="2xl"
                  mt="6px"
                  onPress={() => {
                    if (enrolmentData.enrolment?.enroledPupil?.id) {
                      navigation.push("Account", {
                        accountType: "PUPIL",
                        profileId: enrolmentData.enrolment?.enroledPupil?.id,
                      });
                    }
                  }}
                  px="2"
                >
                  <Text
                    fontSize="md"
                    fontWeight="600"
                    textAlign="center"
                    textDecorationLine="underline"
                  >
                    {"View enroled pupil"}
                  </Text>
                </Pressable>
              )}
              {enrolmentData.enrolment?.enroledParent?.id && (
                <Pressable
                  _hover={{
                    bg: "surface.400",
                  }}
                  _pressed={{
                    bg: "surface.500",
                  }}
                  borderRadius="2xl"
                  onPress={() => {
                    if (enrolmentData.enrolment?.enroledParent?.id) {
                      navigation.navigate("Account", {
                        accountType: "PARENT",
                        profileId: enrolmentData.enrolment?.enroledParent?.id,
                      });
                    }
                  }}
                  px="2"
                >
                  <Text
                    fontSize="md"
                    fontWeight="600"
                    textAlign="center"
                    textDecorationLine="underline"
                  >
                    {"View enroled parent"}
                  </Text>
                </Pressable>
              )}
              {enrolmentData.enrolment?.potentialDuplicates?.edges?.length ||
              enrolmentData.enrolment?.potentialDuplicatePupils?.edges?.length ? (
                <Text mt="6px">{"Potential duplicates:"}</Text>
              ) : null}
              {enrolmentData.enrolment?.potentialDuplicates?.edges?.map((edge) => {
                if (!edge?.node?.id) {
                  return null;
                }

                return (
                  <Pressable
                    _hover={{
                      bg: "surface.400",
                    }}
                    _pressed={{
                      bg: "surface.500",
                    }}
                    borderRadius="2xl"
                    onPress={() => {
                      if (edge.node?.id) {
                        navigation.push("Enrolment", {
                          enrolmentId: edge.node.id,
                        });
                      }
                    }}
                    px="2"
                  >
                    <Text
                      fontSize="md"
                      fontWeight="600"
                      textAlign="center"
                      textDecorationLine="underline"
                    >
                      {`${getFullName(edge.node.pupilFirstName, edge.node.pupilLastName)} (Enrolment)`}
                    </Text>
                  </Pressable>
                );
              })}
              {enrolmentData.enrolment?.potentialDuplicatePupils?.edges?.map((edge) => {
                if (!edge?.node?.id) {
                  return null;
                }

                return (
                  <Pressable
                    _hover={{
                      bg: "surface.400",
                    }}
                    _pressed={{
                      bg: "surface.500",
                    }}
                    borderRadius="2xl"
                    onPress={() => {
                      if (edge.node?.id) {
                        navigation.push("Account", {
                          accountType: "PUPIL",
                          profileId: edge.node.id,
                        });
                      }
                    }}
                    px="2"
                  >
                    <Text
                      fontSize="md"
                      fontWeight="600"
                      textAlign="center"
                      textDecorationLine="underline"
                    >
                      {`${edge.node.primaryParent?.accountNumber ? `#${edge.node.primaryParent?.accountNumber}` : ""} ${getFullName(edge.node.user.firstName, edge.node.user.lastName)} (Account)`}
                    </Text>
                  </Pressable>
                );
              })}
            </VStack>
          </VStack>
          <VStack alignItems="center" space="2" width="100%">
            <TextInput
              autoCapitalize="words"
              id="pupilFirstName"
              initiallyValid={Boolean(enrolmentData.enrolment?.pupilFirstName)}
              initialValue={enrolmentData.enrolment?.pupilFirstName ?? ""}
              invalidIndicator
              label="First name"
              labelStyle={{
                color: "surface.700",
                fontSize: "sm",
              }}
              onFinishEditing={inputFinishEditingHandler}
              size="md"
              updateValueOnInitialValueChange
            />
            <TextInput
              autoCapitalize="words"
              id="pupilLastName"
              initiallyValid
              initialValue={enrolmentData.enrolment?.pupilLastName ?? ""}
              invalidIndicator
              label="Last name"
              labelStyle={{
                color: "surface.700",
                fontSize: "sm",
              }}
              onFinishEditing={inputFinishEditingHandler}
              size="md"
              updateValueOnInitialValueChange
            />
            <TextInput
              id="positionInWaitingList"
              initiallyValid
              initialValue={enrolmentData.enrolment?.positionInWaitingList?.toString() ?? ""}
              intOnly
              label="Waiting List Position"
              labelStyle={{
                color: "surface.700",
                fontSize: "sm",
              }}
              maxLength={500}
              onFinishEditing={inputFinishEditingHandler}
              size="md"
              updateValueOnInitialValueChange
            />
            <>
              <VStack mt="1" space="0.5" width="100%">
                <Text color="surface.700" fontSize="sm" pl="1">
                  School
                </Text>
                <Select
                  bg="surface.100"
                  borderRadius="full"
                  color="surface.900"
                  fontSize="sm"
                  onValueChange={(itemValue) => selectChangeHandler("schoolId", itemValue)}
                  placeholder="⚠️ Not linked to a school"
                  selectedValue={state.values.schoolIdSelect}
                >
                  {allSchools.map((item) => {
                    if (!item?.node?.id) {
                      return null;
                    }

                    const isArchived = item.node.archived;

                    return (
                      <Select.Item
                        key={item.node.id}
                        actionSheetLabel={`${titleCaseConverter(item.node.name)} ${
                          isArchived ? "(Archived)" : ""
                        }`}
                        value={item.node.id}
                      />
                    );
                  })}
                </Select>
                {initialEnrolmentData && !initialEnrolmentData?.schoolIdSelect ? (
                  <Text color="surface.600" flexWrap="wrap" fontSize="sm" mt="1" pl="1">
                    {`⚠️ Match to school before enrolling:\n${enrolmentData.enrolment?.schoolName}, ${enrolmentData.enrolment?.schoolBorough}, ${enrolmentData.enrolment?.schoolPostcode}`}
                  </Text>
                ) : null}
              </VStack>
              <VStack mt="1" space="0.5" width="100%">
                <Text color="surface.700" fontSize="sm" pl="1">
                  School Year
                </Text>
                <Select
                  bg="surface.100"
                  borderRadius="full"
                  color="surface.900"
                  fontSize="sm"
                  onValueChange={(itemValue) => selectChangeHandler("schoolYear", itemValue)}
                  placeholder="Select school year"
                  selectedValue={state.values.schoolYearSelect}
                >
                  {SCHOOL_YEARS.map((item) => {
                    return (
                      <Select.Item
                        key={item.label}
                        actionSheetLabel={item.label}
                        value={item.value}
                      />
                    );
                  })}
                </Select>
              </VStack>
              <TextInput
                autoCapitalize="words"
                id="schoolClass"
                initiallyValid={Boolean(enrolmentData.enrolment?.schoolClass)}
                initialValue={enrolmentData.enrolment?.schoolClass ?? ""}
                invalidIndicator
                label="School Class"
                labelStyle={{
                  color: "surface.700",
                  fontSize: "sm",
                }}
                onFinishEditing={inputFinishEditingHandler}
                size="md"
                updateValueOnInitialValueChange
              />
              <VStack mt="1" space="0.5" width="100%">
                <Text color="surface.700" fontSize="sm" pl="1">
                  Instrument
                </Text>
                <Select
                  bg="surface.100"
                  borderRadius="full"
                  color="surface.900"
                  fontSize="sm"
                  onValueChange={(itemValue) => selectChangeHandler("instrument", itemValue)}
                  placeholder="Select instrument"
                  selectedValue={state.values.instrumentSelect}
                >
                  {INSTRUMENTS.map((item) => {
                    return (
                      <Select.Item
                        key={item?.value}
                        actionSheetLabel={item.label}
                        value={item?.value ?? ""}
                      />
                    );
                  })}
                </Select>
              </VStack>
              <TextInput
                autoCapitalize="words"
                id="parentName"
                initiallyValid={Boolean(enrolmentData.enrolment?.parentName)}
                initialValue={enrolmentData.enrolment?.parentName ?? ""}
                invalidIndicator
                label="Parent Name"
                labelStyle={{
                  color: "surface.700",
                  fontSize: "sm",
                }}
                onFinishEditing={inputFinishEditingHandler}
                size="md"
                updateValueOnInitialValueChange
              />
              <TextInput
                autoCapitalize="words"
                id="parentEmail"
                initiallyValid={Boolean(enrolmentData.enrolment?.parentEmail)}
                initialValue={enrolmentData.enrolment?.parentEmail ?? ""}
                invalidIndicator
                label="Parent Email"
                labelStyle={{
                  color: "surface.700",
                  fontSize: "sm",
                }}
                onFinishEditing={inputFinishEditingHandler}
                size="md"
                updateValueOnInitialValueChange
              />
              <TextInput
                autoCapitalize="words"
                id="parentPhoneNumber"
                initiallyValid={!enrolmentData.enrolment?.hasInvalidParentPhoneNumber}
                initialValue={enrolmentData.enrolment?.parentPhoneNumber ?? ""}
                invalidIndicator
                isInvalid={enrolmentData.enrolment?.hasInvalidParentPhoneNumber}
                label="Parent Phone Number"
                labelStyle={{
                  color: "surface.700",
                  fontSize: "sm",
                }}
                onFinishEditing={inputFinishEditingHandler}
                size="md"
                updateValueOnInitialValueChange
              />
              <VStack mt="1" space="0.5" width="100%">
                <Text color="surface.700" fontSize="sm" pl="1">
                  Requested Lesson Stage
                </Text>
                <Select
                  bg="surface.100"
                  borderRadius="full"
                  color="surface.900"
                  fontSize="sm"
                  onValueChange={(itemValue) => {
                    selectChangeHandler("lessonStage", itemValue);
                  }}
                  placeholder="Select Lesson Stage"
                  selectedValue={state.values.lessonStageSelect}
                >
                  {LESSON_STAGES.map((item) => {
                    return (
                      <Select.Item
                        key={item.value}
                        actionSheetLabel={item.label}
                        value={item.value}
                      />
                    );
                  })}
                </Select>
              </VStack>
              <VStack mt="1" space="0.5" width="100%">
                <Text color="surface.700" fontSize="sm" pl="1">
                  Requested Lesson Type
                </Text>
                <Select
                  bg="surface.100"
                  borderRadius="full"
                  color="surface.900"
                  fontSize="sm"
                  onValueChange={(itemValue) => {
                    selectChangeHandler("lessonType", itemValue);
                  }}
                  placeholder="Select Lesson Type"
                  selectedValue={state.values.lessonTypeSelect}
                >
                  {LESSON_TYPES.map((item) => {
                    return (
                      <Select.Item
                        key={item.value}
                        actionSheetLabel={item.label}
                        value={item.value}
                      />
                    );
                  })}
                </Select>
              </VStack>
              <TextInput
                autoCapitalize="sentences"
                id="adminNotes"
                initiallyValid
                initialValue={enrolmentData.enrolment?.adminNotes ?? ""}
                label="Admin Notes"
                labelStyle={{
                  color: "surface.700",
                  fontSize: "sm",
                }}
                maxLength={500}
                multiline
                numberOfLines={3}
                onFinishEditing={inputFinishEditingHandler}
                size="md"
                updateValueOnInitialValueChange
              />
              <TextInput
                autoCapitalize="sentences"
                id="pupilNotes"
                initiallyValid
                initialValue={enrolmentData.enrolment?.pupilNotes ?? ""}
                label="Pupil Notes"
                labelStyle={{
                  color: "surface.700",
                  fontSize: "sm",
                }}
                maxLength={500}
                multiline
                numberOfLines={3}
                onFinishEditing={inputFinishEditingHandler}
                size="md"
                updateValueOnInitialValueChange
              />
            </>
          </VStack>
        </VStack>
      </ScrollView>
    );
  }, [
    allSchools,
    enrolmentData.enrolment?.adminNotes,
    enrolmentData.enrolment?.archivedTimestamp,
    enrolmentData.enrolment?.createdTimestamp,
    enrolmentData.enrolment?.enroledParent?.id,
    enrolmentData.enrolment?.enroledPupil?.id,
    enrolmentData.enrolment?.enroledTimestamp,
    enrolmentData.enrolment?.hasInvalidParentPhoneNumber,
    enrolmentData.enrolment?.offers.edges,
    enrolmentData.enrolment?.parentEmail,
    enrolmentData.enrolment?.parentName,
    enrolmentData.enrolment?.parentPhoneNumber,
    enrolmentData.enrolment?.positionInWaitingList,
    enrolmentData.enrolment?.potentialDuplicatePupils?.edges,
    enrolmentData.enrolment?.potentialDuplicates?.edges,
    enrolmentData.enrolment?.pupilFirstName,
    enrolmentData.enrolment?.pupilLastName,
    enrolmentData.enrolment?.pupilNotes,
    enrolmentData.enrolment?.schoolBorough,
    enrolmentData.enrolment?.schoolClass,
    enrolmentData.enrolment?.schoolName,
    enrolmentData.enrolment?.schoolPostcode,
    initialEnrolmentData,
    inputFinishEditingHandler,
    navigation,
    renderEnrolmentStatusPressable,
    selectChangeHandler,
    state.values.hasBeenEdited,
    state.values.instrumentSelect,
    state.values.lessonStageSelect,
    state.values.lessonTypeSelect,
    state.values.schoolIdSelect,
    state.values.schoolYearSelect,
    undoChanges,
  ]);

  const renderChangeStatusActionsheet = useMemo(() => {
    return (
      <EnrolmentStatusActionsheet
        editEnrolment={editEnrolment}
        enrolmentId={route.params.enrolmentId}
        enrolmentSchoolIsArchived={enrolmentData.enrolment?.school?.archived ?? false}
        enrolmentsConnectionId={route.params.enrolmentsConnectionId}
        enrolmentStatus={enrolmentStatus}
        enrolPupil={enrolPupil}
        enrolPupilInFlight={enrolPupilInFlight}
        isOpen={state.values.changeStatusActionsheetIsOpen}
        onClose={() =>
          dispatchState({
            input: "changeStatusActionsheetIsOpen",
            value: false,
          })
        }
        onDeleteEnrolment={() => navigation.navigate("WaitingListHub")}
      />
    );
  }, [
    dispatchState,
    editEnrolment,
    enrolPupil,
    enrolPupilInFlight,
    enrolmentData.enrolment?.school?.archived,
    enrolmentStatus,
    navigation,
    route.params.enrolmentId,
    route.params.enrolmentsConnectionId,
    state.values.changeStatusActionsheetIsOpen,
  ]);

  return (
    <Box flex={1} ml={SIDE_BAR_WIDTH}>
      {renderContent}
      {renderChangeStatusActionsheet}
    </Box>
  );
};

const EnrolmentScreen: FC<ScreenProps> = (props) => {
  const { navigation, route } = props;

  const initialState = useMemo(() => {
    return {
      values: {
        changeStatusActionsheetIsOpen: false,
        hasBeenEdited: false,
        lessonStageSelect: "-1",
      },
    };
  }, []);

  const reducer = createReducer<ReducerValues>(initialState);
  const [state, dispatchState] = useReducer(reducer, initialState);

  const [loadEnrolmentQueryReference, loadEnrolmentQuery] =
    useQueryLoader<LoadEnrolmentQuery>(load_enrolment);

  useEffect(() => {
    loadEnrolmentQuery(
      {
        enrolmentId: route.params.enrolmentId,
      },
      { fetchPolicy: "store-or-network" }
    );
  }, [loadEnrolmentQuery, route.params.enrolmentId]);

  return (
    <Box bg="surface.100" flex={1}>
      <SideBar isCollapsed navigation={navigation} width={SIDE_BAR_WIDTH} />
      {loadEnrolmentQueryReference ? (
        <Suspense fallback={<LoadingBlobs pt="70">Loading Enrolment...</LoadingBlobs>}>
          <EnrolmentContent
            dispatchState={dispatchState}
            loadEnrolmentQueryReference={loadEnrolmentQueryReference}
            navigation={navigation}
            route={route}
            state={state}
          />
        </Suspense>
      ) : (
        <LoadingBlobs pt="70">Loading Enrolment...</LoadingBlobs>
      )}
    </Box>
  );
};

export const screenOptions = {
  headerTitle: "Enrolment",
};

export default EnrolmentScreen;
